import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";
import { hasNewBuild } from "../lib/buildVersion";
import { checkUpdateSafety, installActivityTracking } from "../lib/updateSafety";
import type { UnsafeReason } from "../../shared/update-safety";

// 얼마나 자주 묻나 — /version.txt 는 십수 바이트짜리 no-store 파일이라 1분에 한 번 물어도
// 비용이 없다. 2026-09-05 전에는 5분이었는데 "배포했는데 반영이 안 된다"는 체감이 그대로라
// 1분으로 당겼다(배포 → 사용자 화면에 반영까지 최대 1분).
const POLL_MS = 60 * 1000;
// 화면을 옮길 때마다 묻되, 직전 확인에서 이만큼은 지나야 다시 묻는다(연타 방지).
const MIN_GAP_MS = 15 * 1000;
// 새 버전을 안 뒤 "지금 안전한가"를 다시 보는 간격 — 위험해서 미뤄 둔 상태에서 손이 비는
// 순간을 잡으려는 것이다.
const SAFETY_RECHECK_MS = 5 * 1000;
// 자동 새로고침 직전에 "업데이트 중…" 을 잠깐 보여 주는 시간. 화면이 이유 없이 깜빡이는 걸로
// 보이지 않게 하려는 것이고, 그 사이 조작이 들어오면 취소하고 물어본다.
const RELOAD_GRACE_MS = 1500;

/**
 * idle      새 버전 없음
 * waiting   새 버전이 있는데 방금 조작해서(그것만이 이유) 손이 비길 조용히 기다리는 중 — 아무 표시 없음
 * prompt    새 버전이 있는데 지금 새로고침하면 작업이 날아갈 수 있어 모달로 묻는 중
 * deferred  「나중에 하기」를 골랐다 — 헤더 버튼만 남기고, 손이 비면 조용히 자동 새로고침
 * reloading 곧 새로고침한다("업데이트 중…" 표시)
 */
export type NewBuildPhase = "idle" | "waiting" | "prompt" | "deferred" | "reloading";

/** 이것만이 이유면 묻지 않고 기다린다 — 몇 초 뒤 저절로 풀리는 상태라 물어볼 가치가 없다. */
const SOFT_REASONS: ReadonlySet<UnsafeReason> = new Set(["recent-activity"]);
function onlySoft(reasons: UnsafeReason[]): boolean {
  return reasons.every((r) => SOFT_REASONS.has(r));
}

interface NewBuildState {
  phase: NewBuildPhase;
  /** 자동으로 넘기지 않은 이유(모달에 사람 말로 보여 준다). 없으면 빈 배열. */
  reasons: UnsafeReason[];
  /** 「지금 업데이트」 — 통째로 새로고침해 새 코드를 받는다. */
  reload: () => void;
  /** 「나중에 하기」 — 모달을 닫고 헤더 버튼으로 내려앉는다(손이 비면 자동으로 넘어간다). */
  dismiss: () => void;
}

const NewBuildContext = createContext<NewBuildState>({
  phase: "idle",
  reasons: [],
  reload: () => window.location.reload(),
  dismiss: () => {},
});

/**
 * "새 버전이 나갔나"를 한 곳에서 지켜보고, 안전하면 조용히 새로고침하고 위험하면 묻는다.
 * 모달(NewBuildModal)과 헤더 버튼(NewBuildHeaderButton)이 이 상태 하나를 본다.
 *
 * 왜: 화면과 서버가 같은 이미지로 함께 나가도, 배포 **전**부터 열려 있던 탭은 옛 자바스크립트를
 * 계속 쓴다. 그 탭이 새 서버의 달라진 응답을 받으면 화면 한가운데서 터진다(2026-09-03 구간표
 * 업로드). 배포 뒤 옛 탭이 스스로 알아채고 새 코드로 갈아타는 게 유일한 방어다.
 *
 * 흐름(2026-09-05 결정 — "안전하면 자동, 위험하면 묻는다"):
 *  1. 1분마다 + 탭 복귀 + 화면 이동(15초 간격) 때 /version.txt 를 견줘 새 버전을 알아챈다.
 *  2. 알아챈 순간 checkUpdateSafety 로 "지금 새로고침해도 작업이 안 날아가나"를 본다
 *     (판정 규칙은 shared/update-safety.ts). 안전 → 1.5초 "업데이트 중…" 뒤 새로고침.
 *     "방금 조작했다"만이 이유면(로그인 직후·메뉴를 막 누름) 묻지 않고 5초마다 다시 본다(waiting) —
 *     그 사이 입력·팝업 같은 진짜 이유가 생기면 그때 묻는다.
 *     그 밖의 위험 → 모달로 묻는다(왜 자동으로 안 넘겼는지 첫 이유를 같이 보여 준다).
 *  3. 「나중에 하기」 뒤에는 헤더 버튼만 남기고 5초마다 다시 본다 — 저장을 마치고 창을 닫아
 *     손이 비면 그때 조용히 넘긴다. 사용자가 버튼을 안 눌러도 언젠가는 새 코드로 간다.
 *     한 번 미룬 사람에겐 다시 묻지 않는다.
 *
 * 새로고침은 주소를 그대로 유지하므로(모달·탭 상태도 주소에 실린다 — 프론트 공통 규칙) 보던
 * 자리로 돌아온다. 로컬 개발(dev 빌드)에서는 hasNewBuild 가 항상 false 라 아무것도 안 뜬다.
 */
export function NewBuildProvider({ children }: { children: ReactNode }) {
  const [available, setAvailable] = useState(false);
  const [phase, setPhase] = useState<NewBuildPhase>("idle");
  const [reasons, setReasons] = useState<UnsafeReason[]>([]);
  const lastCheckRef = useRef(0);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    installActivityTracking();
  }, []);

  const reload = useCallback(() => {
    setPhase("reloading");
    window.location.reload();
  }, []);

  // ── 1) 새 버전 감지 ─────────────────────────────────────────────
  const probe = useCallback(async () => {
    if (document.visibilityState === "hidden") return;
    lastCheckRef.current = Date.now();
    if (await hasNewBuild()) setAvailable(true);
  }, []);

  useEffect(() => {
    if (available) return;
    let disposed = false;
    const run = () => {
      if (!disposed) void probe();
    };
    const timer = setInterval(run, POLL_MS);
    document.addEventListener("visibilitychange", run);
    window.addEventListener("focus", run);
    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", run);
      window.removeEventListener("focus", run);
    };
  }, [available, probe]);

  // 화면을 옮길 때 — 배포 직후 메뉴를 누르는 사용자가 1분 타이머를 기다리지 않게 한다.
  useEffect(() => {
    if (available) return;
    if (Date.now() - lastCheckRef.current < MIN_GAP_MS) return;
    void probe();
  }, [pathname, available, probe]);

  // ── 2) 안전하면 자동, 위험하면 묻기 ────────────────────────────
  // 새 버전을 안 직후(phase idle) 한 번, 기다리는 중(waiting)·「나중에 하기」 뒤(deferred)엔 5초마다.
  // 안전하면 phase 를 reloading 으로 바꾸기만 한다 — 실제 새로고침은 아래 3) 이 한다.
  // ⚠️ 여기서 타이머를 걸면 안 된다: setPhase 로 이 effect 가 다시 돌면서 정리 단계가 방금 건
  //    타이머를 지워 버려, 알약만 남고 영영 새로고침이 안 된다(2026-09-05 검증에서 실제로 걸렸다).
  const resumePhaseRef = useRef<NewBuildPhase>("idle");
  useEffect(() => {
    if (!available) return;
    if (phase !== "idle" && phase !== "waiting" && phase !== "deferred") return;

    const attempt = () => {
      const verdict = checkUpdateSafety(pathnameRef.current);
      if (!verdict.safe) {
        setReasons(verdict.reasons);
        // 이미 미뤄 둔 상태(deferred)면 다시 묻지 않고 다음 회차를 기다린다.
        // 그 전엔 "방금 조작했다"뿐이면 조용히 기다리고(waiting), 진짜 이유가 있으면 묻는다.
        if (phase !== "deferred") setPhase(onlySoft(verdict.reasons) ? "waiting" : "prompt");
        return;
      }
      resumePhaseRef.current = phase;
      setPhase("reloading");
    };

    attempt();
    const timer = phase === "idle" ? undefined : setInterval(attempt, SAFETY_RECHECK_MS);
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [available, phase]);

  // ── 3) 자동 새로고침 — 잠깐 "업데이트 중…"을 보여 준 뒤 넘긴다 ──────
  // 그 사이 조작이 들어오면(마우스를 잡았다) 위험으로 돌아섰을 수 있으니 한 번 더 보고,
  // 위험하면 원래 있던 자리(미뤄 둔 상태면 그대로, 방금 조작한 것뿐이면 기다림, 그 밖엔 묻기)로 돌아간다.
  useEffect(() => {
    if (phase !== "reloading") return;
    const timer = setTimeout(() => {
      const again = checkUpdateSafety(pathnameRef.current);
      if (again.safe) {
        window.location.reload();
        return;
      }
      setReasons(again.reasons);
      const from = resumePhaseRef.current;
      setPhase(from === "deferred" ? "deferred" : onlySoft(again.reasons) ? "waiting" : "prompt");
    }, RELOAD_GRACE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const value = useMemo<NewBuildState>(
    () => ({
      phase,
      reasons,
      reload,
      dismiss: () => setPhase("deferred"),
    }),
    [phase, reasons, reload],
  );

  return <NewBuildContext.Provider value={value}>{children}</NewBuildContext.Provider>;
}

export function useNewBuild(): NewBuildState {
  return useContext(NewBuildContext);
}
